/**
 * Messages between the main thread and the layout worker, and the parsers that check them on arrival in each
 * direction (Definition of Done, security: nothing unvalidated crosses a boundary; part 12 spec: the worker
 * message schema is validated both ways). Every request carries an ID its response repeats, so answers can
 * arrive in any order and a late one can't pass for another's. Table P in the part 12 log; the preview's messages
 * (open, edit, view, settle, close) are table V in the part 15 log, the book view's (shelf, spread) its rows V10–V12 in
 * the part 15a log.
 */
import { at } from '../arrays.ts';
import type { FaceMetrics } from '../font/face.types.ts';
import { FONT_STYLES } from '../font/faces.ts';
import type { FaceKey } from '../font/faces.ts';
import { MAX_GLYPHS } from '../font/limits.ts';
import type { GlyphRun } from '../font/shape.ts';
import { FONT_IDS } from '../theme/fonts.ts';
import { BOOK_LANGUAGES, METADATA_TEXT } from '../theme/metadata.ts';
import type { BookLanguage } from '../theme/metadata.ts';
import { DEVICE_PROFILES } from '../theme/profiles.ts';
import type { ProfileId } from '../theme/profiles.ts';
import { booleanField, choiceField, readFields, RecordError } from '../theme/record.ts';
import type { Fields } from '../theme/record.ts';
import { parseTheme } from '../theme/theme.ts';
import type { Theme } from '../theme/theme.ts';
import type { TextChange } from '@cockatrice/core';
import type { BookChapter, PdfBook } from '../book/pages.ts';
import type { BookMode, Place } from '../book/spreads.ts';
import { parseMetadata } from '../theme/metadata.ts';

/** A face that has loaded, as the main thread sees it: no tables, its metrics and whether it has true small caps. */
export interface LoadedFace {
  readonly face: FaceKey;
  readonly metrics: FaceMetrics;
  readonly hasSmallCaps: boolean;
}

/**
 * What a preview session lays a chapter out with (part 14's `PageSettings` as it crosses the boundary): the book, its
 * theme, the device profile, the book's language, the chapter's title and its place in the book (V2). The book's ID
 * (part 16b, table V2) keys the images its text names: an image is only ever one of its own book's (decision 37).
 */
export interface PreviewSettings {
  readonly book: string;
  readonly theme: Theme;
  readonly profile: ProfileId;
  readonly language: BookLanguage;
  readonly title: string;
  readonly subtitle: string;
  readonly image: string;
  readonly number: number;
}

/**
 * A drawn page's pixels as they cross from the worker: an `ImageBitmap`, transferred. The layout package doesn't see the
 * DOM's types, so it is an object here; arriving, it is checked to be an `ImageBitmap` (V9).
 */
export type Bitmap = object;

/**
 * A page as the book view sends it (V12): its place in the chapter (from 0), whether that is exact or an estimate
 * (decision 10), and its drawing: `draw` names it, `bitmap` carries its pixels and `text` what the page reads as (part
 * 11c, A1), both null when the main thread holds draw `draw` already.
 */
interface DrawnPage<B extends object> {
  readonly index: number;
  readonly exact: boolean;
  readonly draw: number;
  readonly bitmap: B | null;
  readonly text: string | null;
}

/** A chapter the book view lays out read-only beside the session (part 15a, Q20): its settings and its stored text. */
export interface ShelfChapter {
  readonly settings: PreviewSettings;
  readonly text: string;
}

/** A page drawing the book view still shows (V11, Q17): a page of any chapter, so its chapter names it with its index. */
export interface HeldBookDraw {
  readonly chapter: number;
  readonly index: number;
  readonly draw: number;
}

/**
 * One side of a look in the book view (V12; part 15a, table K): a chapter's {@link DrawnPage}, the
 * blank page before a chapter, nothing, or a page not laid out yet.
 */
export type BookSlot<B extends object = Bitmap> =
  | (DrawnPage<B> & { readonly kind: 'page'; readonly chapter: number })
  | { readonly kind: 'blank'; readonly chapter: number }
  | { readonly kind: 'none' }
  | { readonly kind: 'pending' };

/** Main thread → layout worker. */
export type LayoutRequest =
  | { readonly id: number; readonly kind: 'load'; readonly faces: readonly FaceKey[] }
  | {
      readonly id: number;
      readonly kind: 'shape';
      readonly face: FaceKey;
      readonly text: string;
      readonly smallCaps: boolean;
      readonly language: BookLanguage | null;
    }
  | { readonly id: number; readonly kind: 'open'; readonly chapter: number; readonly text: string; readonly settings: PreviewSettings }
  | { readonly id: number; readonly kind: 'edit'; readonly chapter: number; readonly changes: readonly TextChange[] }
  | { readonly id: number; readonly kind: 'settle'; readonly chapter: number }
  | { readonly id: number; readonly kind: 'close'; readonly chapter: number }
  | { readonly id: number; readonly kind: 'shelf'; readonly session: number; readonly chapters: readonly ShelfChapter[] }
  | {
      readonly id: number;
      readonly kind: 'spread';
      readonly session: number;
      readonly cursor: number;
      readonly place: Place | null;
      readonly turn: number;
      readonly mode: BookMode;
      readonly chapters: number;
      readonly scale: number;
      readonly held: readonly HeldBookDraw[];
      /** A page drawn apart from the view (part 15b, CU13): the worker's record of what the view holds stays as it was. */
      readonly apart?: true;
    }
  | { readonly id: number; readonly kind: 'pdf'; readonly book: PdfBook }
  | { readonly id: number; readonly kind: 'cancel'; readonly job: number };

/** Layout worker → main thread; `B` is the bitmap type the worker side sends. */
export type LayoutResponse<B extends object = Bitmap> =
  | { readonly id: number; readonly kind: 'loaded'; readonly faces: readonly LoadedFace[] }
  | { readonly id: number; readonly kind: 'shaped'; readonly run: GlyphRun }
  | { readonly id: number; readonly kind: 'done' }
  | { readonly id: number; readonly kind: 'stale' }
  | {
      readonly id: number;
      readonly kind: 'spread';
      readonly units: readonly (readonly BookSlot<B>[])[];
      readonly current: number;
      readonly place: Place | null;
      readonly turned: number;
      readonly start: boolean;
      readonly end: boolean;
      readonly unsettled: boolean;
    }
  | { readonly id: number; readonly kind: 'settled'; readonly complete: boolean }
  | { readonly id: number; readonly kind: 'progress'; readonly done: number; readonly total: number }
  | { readonly id: number; readonly kind: 'pdf'; readonly bytes: ArrayBuffer }
  | { readonly id: number; readonly kind: 'cancelled' }
  | { readonly id: number; readonly kind: 'error'; readonly message: string };

/** Thrown for a message that doesn't match the protocol. */
export class ProtocolError extends Error {
  override readonly name = 'ProtocolError';
}

/** Longest text one shape request carries (UTF-16 units): a paragraph far longer than any book holds. */
export const MAX_SHAPE_TEXT = 100_000;
/** Longest error message carried; a longer one is cut. */
export const MAX_MESSAGE_LENGTH = 2_000;
/** Most faces one load request names: every curated face. */
export const MAX_LOAD_FACES = FONT_IDS.length * FONT_STYLES.length;
/** Longest chapter text a preview takes (UTF-16 units): about 1.5 million words, far past the long-chapter warning. */
export const MAX_CHAPTER_TEXT = 10_000_000;
/** Most chapters a shelf holds (V10): the three beside the shown unit's, and one to spare. */
export const MAX_SHELF = 4;
/** Most units one spread request turns (V11, V12): far more than presses add up while a request is answered. */
export const MAX_TURN = 16;
/** Most units a spread answer holds (V12): the shown unit and one on each side (U5). */
export const MAX_UNITS = 3;
/** Most slots a unit holds (V12): a spread's two. */
export const MAX_SLOTS = 2;
/** Most changes one edit carries; CodeMirror's change sets from typing, paste and undo hold far fewer. */
export const MAX_CHANGES = 10_000;
/** A chapter's place in the book is at most this; a book holds far fewer chapters. */
export const MAX_CHAPTER_NUMBER = 100_000;
/** Most drawings a view request says it holds, and most pages a view answers with (V4, V8). */
export const MAX_VIEW_PAGES = 16;
/** Canvas pixels per point a view may ask for (V4): from a thumbnail to a magnified page on a dense screen. */
export const MIN_SCALE = 0.05;
export const MAX_SCALE = 16;
/** Most chapters an export carries (E1): as many as a book can number. */
export const MAX_BOOK_CHAPTERS = MAX_CHAPTER_NUMBER;
/** Longest book text an export carries in all (E1, UTF-16 units): about eight million words. */
export const MAX_BOOK_TEXT = 50_000_000;
/** E4: what every PDF starts with. */
export const PDF_HEADER = '%PDF-';
const PROFILE_IDS: readonly ProfileId[] = DEVICE_PROFILES.map((profile) => profile.id);

const LOAD_KEYS = ['id', 'kind', 'faces'] as const;
const SHAPE_KEYS = ['id', 'kind', 'face', 'text', 'smallCaps', 'language'] as const;
const FACE_KEYS = ['font', 'style'] as const;
const LOADED_KEYS = ['id', 'kind', 'faces'] as const;
const LOADED_FACE_KEYS = ['face', 'metrics', 'hasSmallCaps'] as const;
const METRICS_KEYS = ['unitsPerEm', 'ascent', 'descent', 'lineGap', 'capHeight', 'xHeight', 'numGlyphs'] as const;
const SHAPED_KEYS = ['id', 'kind', 'run'] as const;
const RUN_KEYS = ['glyphIds', 'clusters', 'advances', 'xOffsets', 'yOffsets', 'scales'] as const;
const ERROR_KEYS = ['id', 'kind', 'message'] as const;
const OPEN_KEYS = ['id', 'kind', 'chapter', 'text', 'settings'] as const;
const SETTINGS_KEYS = ['book', 'theme', 'profile', 'language', 'title', 'subtitle', 'image', 'number'] as const;
const EDIT_KEYS = ['id', 'kind', 'chapter', 'changes'] as const;
const CHANGE_KEYS = ['from', 'to', 'insert'] as const;
const CHAPTER_KEYS = ['id', 'kind', 'chapter'] as const;
const ACK_KEYS = ['id', 'kind'] as const;
const SETTLED_KEYS = ['id', 'kind', 'complete'] as const;
const SHELF_KEYS = ['id', 'kind', 'session', 'chapters'] as const;
const SHELF_CHAPTER_KEYS = ['settings', 'text'] as const;
const SPREAD_KEYS = ['id', 'kind', 'session', 'cursor', 'place', 'turn', 'mode', 'chapters', 'scale', 'held'] as const;
const SPREAD_APART_KEYS = [...SPREAD_KEYS, 'apart'] as const;
const PLACE_KEYS = ['chapter', 'page'] as const;
const HELD_BOOK_KEYS = ['chapter', 'index', 'draw'] as const;
const SPREAD_ANSWER_KEYS = ['id', 'kind', 'units', 'current', 'place', 'turned', 'start', 'end', 'unsettled'] as const;
const SLOT_PAGE_KEYS = ['kind', 'chapter', 'index', 'exact', 'draw', 'bitmap', 'text'] as const;
const SLOT_BLANK_KEYS = ['kind', 'chapter'] as const;
const SLOT_EMPTY_KEYS = ['kind'] as const;
const BOOK_MODES: readonly BookMode[] = ['spread', 'page'];
const PDF_KEYS = ['id', 'kind', 'book'] as const;
const BOOK_KEYS = ['id', 'theme', 'metadata', 'chapters'] as const;
const BOOK_CHAPTER_KEYS = ['title', 'subtitle', 'image', 'text'] as const;
const CANCEL_KEYS = ['id', 'kind', 'job'] as const;
const PROGRESS_KEYS = ['id', 'kind', 'done', 'total'] as const;
const BYTES_KEYS = ['id', 'kind', 'bytes'] as const;

/** Runs a parse, reporting a malformed object (`readFields` and friends) as a {@link ProtocolError}. */
function parsing<T>(parse: () => T): T {
  try {
    return parse();
  } catch (error) {
    if (error instanceof RecordError) throw new ProtocolError(error.message);
    throw error;
  }
}

/** The message's `kind`, read before its other fields decide which keys it may have (P2). */
function kindOf(value: unknown, what: string): string {
  if (typeof value !== 'object' || value === null) throw new ProtocolError(`${what} must be an object`);
  const descriptor = Object.getOwnPropertyDescriptor(value, 'kind');
  const kind: unknown = descriptor !== undefined && 'value' in descriptor ? descriptor.value : undefined;
  if (typeof kind !== 'string') throw new ProtocolError(`${what} has no kind`);
  return kind;
}

/** A request or response ID: a whole number from 0 to 2^53 − 1 (P3). */
function idField(fields: Fields): number {
  const id = fields.get('id');
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id < 0) throw new ProtocolError('id must be a whole number from 0');
  return id + 0;
}

function faceField(value: unknown, path: string): FaceKey {
  const fields = readFields(value, FACE_KEYS, 'a face', path);
  return { font: choiceField(fields, 'font', FONT_IDS), style: choiceField(fields, 'style', FONT_STYLES) };
}

function faceList<T>(value: unknown, path: string, item: (entry: unknown, path: string) => T): T[] {
  if (!Array.isArray(value)) throw new ProtocolError(`${path} must be an array`);
  if (value.length > MAX_LOAD_FACES) throw new ProtocolError(`${path} has more than ${MAX_LOAD_FACES} entries`);
  return value.map((entry: unknown, i) => item(entry, `${path}.${i}`));
}

function textField(fields: Fields): string {
  const text = fields.get('text');
  if (typeof text !== 'string') throw new ProtocolError('text must be a string');
  if (text.length > MAX_SHAPE_TEXT) throw new ProtocolError(`text is longer than ${MAX_SHAPE_TEXT}`);
  return text;
}

function languageField(fields: Fields): BookLanguage | null {
  return fields.get('language') === null ? null : choiceField(fields, 'language', BOOK_LANGUAGES);
}

/** A string of at most `max` UTF-16 units. */
function stringField(fields: Fields, key: string, max: number): string {
  const value = fields.get(key);
  if (typeof value !== 'string') throw new ProtocolError(`${key} must be a string`);
  if (value.length > max) throw new ProtocolError(`${key} is longer than ${max}`);
  return value;
}

/** A session, drawing or page number: a whole number from 0 to 2^53 − 1, −0 read as 0, as request IDs are (V3). */
function numberField(fields: Fields, key: string): number {
  return wholeField(fields, key, 0, Number.MAX_SAFE_INTEGER) + 0;
}

/** Part 16b, V2: a book's ID, as the store writes one: 32 lowercase hex digits. */
function bookIdField(fields: Fields, key: string, path: string): string {
  const value = fields.get(key);
  if (typeof value !== 'string' || !/^[0-9a-f]{32}$/u.test(value)) throw new ProtocolError(`${path} must be 32 lowercase hex digits`);
  return value;
}

/** T‴: the longest an image reference can be: `image:`, 32 digits, `?sha256=` and 64. */
const MAX_IMAGE_REFERENCE = 110;

/** V2: the settings, the theme checked field by field as part 05 reads a stored one. */
function settingsField(value: unknown): PreviewSettings {
  const fields = readFields(value, SETTINGS_KEYS, 'preview settings', 'settings');
  return {
    book: bookIdField(fields, 'book', 'settings.book'),
    theme: parseTheme(fields.get('theme')),
    profile: choiceField(fields, 'profile', PROFILE_IDS),
    language: choiceField(fields, 'language', BOOK_LANGUAGES),
    title: stringField(fields, 'title', METADATA_TEXT.title.max),
    subtitle: stringField(fields, 'subtitle', METADATA_TEXT.title.max),
    image: stringField(fields, 'image', MAX_IMAGE_REFERENCE),
    number: wholeField(fields, 'number', 1, MAX_CHAPTER_NUMBER),
  };
}

/** V3: an edit's changes, each a range in the chapter and what replaces it; their order is the worker's to check. */
function changesField(value: unknown): TextChange[] {
  if (!Array.isArray(value)) throw new ProtocolError('changes must be an array');
  if (value.length > MAX_CHANGES) throw new ProtocolError(`changes has more than ${MAX_CHANGES} entries`);
  let inserted = 0;
  return value.map((entry: unknown, i): TextChange => {
    const fields = readFields(entry, CHANGE_KEYS, 'a change', `changes.${i}`);
    const from = wholeField(fields, 'from', 0, MAX_CHAPTER_TEXT);
    const to = wholeField(fields, 'to', from, MAX_CHAPTER_TEXT);
    const insert = stringField(fields, 'insert', MAX_CHAPTER_TEXT);
    inserted += insert.length;
    if (inserted > MAX_CHAPTER_TEXT) throw new ProtocolError(`changes insert more than ${MAX_CHAPTER_TEXT}`);
    return { from, to, insert };
  });
}

/** V10: the shelf's chapters, each with settings as an open's (V2) and a chapter's text, no number twice. */
function shelfField(value: unknown): ShelfChapter[] {
  if (!Array.isArray(value)) throw new ProtocolError('chapters must be an array');
  if (value.length > MAX_SHELF) throw new ProtocolError(`chapters has more than ${MAX_SHELF} entries`);
  const chapters = value.map((entry: unknown, i): ShelfChapter => {
    const fields = readFields(entry, SHELF_CHAPTER_KEYS, 'a shelf chapter', `chapters.${i}`);
    return { settings: settingsField(fields.get('settings')), text: stringField(fields, 'text', MAX_CHAPTER_TEXT) };
  });
  if (new Set(chapters.map((chapter) => chapter.settings.number)).size !== chapters.length) throw new ProtocolError('chapters names a chapter twice');
  return chapters;
}

/** V11: a place in the book: a chapter, and its page from −1 (the blank before it). */
function placeField(value: unknown, path: string): Place {
  const fields = readFields(value, PLACE_KEYS, 'a place', path);
  return { chapter: wholeField(fields, 'chapter', 1, MAX_CHAPTER_NUMBER), page: wholeField(fields, 'page', -1, Number.MAX_SAFE_INTEGER) + 0 };
}

/** V11: the book view's held drawings, each page of a chapter once. */
function heldBookField(value: unknown): HeldBookDraw[] {
  if (!Array.isArray(value)) throw new ProtocolError('held must be an array');
  if (value.length > MAX_VIEW_PAGES) throw new ProtocolError(`held has more than ${MAX_VIEW_PAGES} entries`);
  const held = value.map((entry: unknown, i): HeldBookDraw => {
    const fields = readFields(entry, HELD_BOOK_KEYS, 'a held drawing', `held.${i}`);
    return { chapter: wholeField(fields, 'chapter', 1, MAX_CHAPTER_NUMBER), index: numberField(fields, 'index'), draw: numberField(fields, 'draw') };
  });
  if (new Set(held.map((each) => `${each.chapter}:${each.index}`)).size !== held.length) throw new ProtocolError('held names a page twice');
  return held;
}

/** V4, V11: a scale a view may ask for. */
function scaleField(fields: Fields): number {
  const scale = finiteField(fields, 'scale');
  if (scale < MIN_SCALE || scale > MAX_SCALE) throw new ProtocolError(`scale must be from ${MIN_SCALE} to ${MAX_SCALE}`);
  return scale;
}

/** E1: a book to export: its theme and metadata read as part 05 reads stored ones, its chapters within the limits. */
function bookField(value: unknown): PdfBook {
  const fields = readFields(value, BOOK_KEYS, 'a book', 'book');
  const chapters = fields.get('chapters');
  if (!Array.isArray(chapters)) throw new ProtocolError('book.chapters must be an array');
  if (chapters.length > MAX_BOOK_CHAPTERS) throw new ProtocolError(`book.chapters has more than ${MAX_BOOK_CHAPTERS} entries`);
  let length = 0;
  return {
    id: bookIdField(fields, 'id', 'book.id'),
    theme: parseTheme(fields.get('theme')),
    metadata: parseMetadata(fields.get('metadata')),
    chapters: chapters.map((entry: unknown, i): BookChapter => {
      const chapter = readFields(entry, BOOK_CHAPTER_KEYS, 'a chapter', `book.chapters.${i}`);
      const text = stringField(chapter, 'text', MAX_CHAPTER_TEXT);
      length += text.length;
      if (length > MAX_BOOK_TEXT) throw new ProtocolError(`book.chapters hold more than ${MAX_BOOK_TEXT} units of text`);
      return { title: stringField(chapter, 'title', METADATA_TEXT.title.max), subtitle: stringField(chapter, 'subtitle', METADATA_TEXT.title.max), image: stringField(chapter, 'image', MAX_IMAGE_REFERENCE), text };
    }),
  };
}

/**
 * Part 20, E1: a book to export read as the PDF's E1 reads it (exactly its fields; the theme and metadata as part 05 reads
 * stored ones; the same limits), for the EPUB worker; a `ProtocolError` for anything else.
 */
export function readExportBook(value: unknown): PdfBook {
  return parsing(() => bookField(value));
}

/** Parses a request arriving at the layout worker (P1–P8, V1–V5, E1, E2). */
export function parseLayoutRequest(value: unknown): LayoutRequest {
  return parsing(() => {
    const kind = kindOf(value, 'layout request');
    if (kind === 'load') {
      const fields = readFields(value, LOAD_KEYS, 'a load request');
      const faces = faceList(fields.get('faces'), 'faces', faceField);
      if (faces.length === 0) throw new ProtocolError('faces must name at least one face');
      return { id: idField(fields), kind, faces };
    }
    if (kind === 'shape') {
      const fields = readFields(value, SHAPE_KEYS, 'a shape request');
      return {
        id: idField(fields),
        kind,
        face: faceField(fields.get('face'), 'face'),
        text: textField(fields),
        smallCaps: booleanField(fields, 'smallCaps'),
        language: languageField(fields),
      };
    }
    if (kind === 'open') {
      const fields = readFields(value, OPEN_KEYS, 'an open request');
      return {
        id: idField(fields),
        kind,
        chapter: numberField(fields, 'chapter'),
        text: stringField(fields, 'text', MAX_CHAPTER_TEXT),
        settings: settingsField(fields.get('settings')),
      };
    }
    if (kind === 'edit') {
      const fields = readFields(value, EDIT_KEYS, 'an edit request');
      return { id: idField(fields), kind, chapter: numberField(fields, 'chapter'), changes: changesField(fields.get('changes')) };
    }
    if (kind === 'shelf') {
      const fields = readFields(value, SHELF_KEYS, 'a shelf request');
      return { id: idField(fields), kind, session: numberField(fields, 'session'), chapters: shelfField(fields.get('chapters')) };
    }
    if (kind === 'spread') {
      const marked = typeof value === 'object' && value !== null && Object.hasOwn(value, 'apart');
      const fields = readFields(value, marked ? SPREAD_APART_KEYS : SPREAD_KEYS, 'a spread request');
      const place = fields.get('place');
      const turn = wholeField(fields, 'turn', -MAX_TURN, MAX_TURN) + 0;
      // T4: following the cursor turns nothing; a turn starts from a place.
      if (place === null && turn !== 0) throw new ProtocolError('a turn needs a place');
      if (marked && fields.get('apart') !== true) throw new ProtocolError('apart: must be true');
      return {
        ...(marked ? { apart: true } : {}),
        id: idField(fields),
        kind,
        session: numberField(fields, 'session'),
        cursor: wholeField(fields, 'cursor', 0, MAX_CHAPTER_TEXT),
        place: place === null ? null : placeField(place, 'place'),
        turn,
        mode: choiceField(fields, 'mode', BOOK_MODES),
        chapters: wholeField(fields, 'chapters', 1, MAX_CHAPTER_NUMBER),
        scale: scaleField(fields),
        held: heldBookField(fields.get('held')),
      };
    }
    if (kind === 'settle' || kind === 'close') {
      const fields = readFields(value, CHAPTER_KEYS, `a ${kind} request`);
      return { id: idField(fields), kind, chapter: numberField(fields, 'chapter') };
    }
    if (kind === 'pdf') {
      const fields = readFields(value, PDF_KEYS, 'a pdf request');
      return { id: idField(fields), kind, book: bookField(fields.get('book')) };
    }
    if (kind === 'cancel') {
      const fields = readFields(value, CANCEL_KEYS, 'a cancel request');
      return { id: idField(fields), kind, job: numberField(fields, 'job') };
    }
    throw new ProtocolError(`unknown layout request "${kind.slice(0, 64)}"`);
  });
}

function finiteField(fields: Fields, key: string): number {
  const value = fields.get(key);
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ProtocolError(`${key} must be a finite number`);
  return value;
}

function wholeField(fields: Fields, key: string, min: number, max: number): number {
  const value = finiteField(fields, key);
  if (!Number.isInteger(value) || value < min || value > max) throw new ProtocolError(`${key} must be a whole number from ${min} to ${max}`);
  return value;
}

function metricsField(value: unknown, path: string): FaceMetrics {
  const fields = readFields(value, METRICS_KEYS, 'metrics', path);
  return {
    unitsPerEm: wholeField(fields, 'unitsPerEm', 16, 16_384),
    ascent: finiteField(fields, 'ascent'),
    descent: finiteField(fields, 'descent'),
    lineGap: finiteField(fields, 'lineGap'),
    capHeight: finiteField(fields, 'capHeight'),
    xHeight: finiteField(fields, 'xHeight'),
    numGlyphs: wholeField(fields, 'numGlyphs', 1, MAX_GLYPHS),
  };
}

function loadedFace(value: unknown, path: string): LoadedFace {
  const fields = readFields(value, LOADED_FACE_KEYS, 'a loaded face', path);
  return { face: faceField(fields.get('face'), `${path}.face`), metrics: metricsField(fields.get('metrics'), `${path}.metrics`), hasSmallCaps: booleanField(fields, 'hasSmallCaps') };
}

/** An array of `type` over its own (not shared) memory, so it can be kept and transferred (P6). */
function typedField<T extends Uint16Array<ArrayBuffer> | Uint32Array<ArrayBuffer> | Float64Array<ArrayBuffer>>(
  fields: Fields,
  key: string,
  type: abstract new (...args: never[]) => T,
): T {
  const value = fields.get(key);
  if (!(value instanceof type) || !(value.buffer instanceof ArrayBuffer)) throw new ProtocolError(`run.${key} has the wrong array type`);
  return value;
}

/** A glyph run: equal lengths, clusters starting at 0 and never decreasing, finite placements, scales in (0, 1] (P7). */
function runField(value: unknown): GlyphRun {
  const fields = readFields(value, RUN_KEYS, 'a run', 'run');
  const run: GlyphRun = {
    glyphIds: typedField(fields, 'glyphIds', Uint16Array),
    clusters: typedField(fields, 'clusters', Uint32Array),
    advances: typedField(fields, 'advances', Float64Array),
    xOffsets: typedField(fields, 'xOffsets', Float64Array),
    yOffsets: typedField(fields, 'yOffsets', Float64Array),
    scales: typedField(fields, 'scales', Float64Array),
  };
  const count = run.glyphIds.length;
  if ([run.clusters, run.advances, run.xOffsets, run.yOffsets, run.scales].some((array) => array.length !== count)) {
    throw new ProtocolError('run: array lengths differ');
  }
  const clusters = run.clusters;
  if (clusters.some((cluster, i) => (i === 0 ? cluster !== 0 : cluster < at(clusters, i - 1)) || cluster > MAX_SHAPE_TEXT)) {
    throw new ProtocolError('run.clusters must start at 0, never decrease and stay in the text');
  }
  for (const array of [run.advances, run.xOffsets, run.yOffsets]) {
    if (!array.every(Number.isFinite)) throw new ProtocolError('run: a placement is not a finite number');
  }
  if (!run.scales.every((scale) => scale > 0 && scale <= 1)) throw new ProtocolError('run.scales must be in (0, 1]');
  return run;
}

/** V9: whether a value is the platform's `ImageBitmap` (none outside a browser, so nothing is one there). */
export function isBitmap(value: unknown): value is Bitmap {
  const type: unknown = Reflect.get(globalThis, 'ImageBitmap');
  return typeof type === 'function' && value instanceof type;
}

/** V8, V12: a drawn page's bitmap and text: a bitmap with its text, or neither for a drawing the main thread holds. */
function drawingFields(fields: Fields, path: string): { readonly bitmap: Bitmap | null; readonly text: string | null } {
  const bitmap = fields.get('bitmap');
  if (bitmap !== null && !isBitmap(bitmap)) throw new ProtocolError(`${path}.bitmap must be an ImageBitmap or null`);
  if (bitmap === null && fields.get('text') !== null) throw new ProtocolError(`${path}.text must be null for a held page`);
  return { bitmap, text: bitmap === null ? null : stringField(fields, 'text', MAX_CHAPTER_TEXT) };
}

/** V12: one side of a look, with exactly its kind's fields. */
function slotField(value: unknown, path: string): BookSlot {
  const kind = kindOf(value, path);
  if (kind === 'page') {
    const fields = readFields(value, SLOT_PAGE_KEYS, 'a page slot', path);
    return {
      kind,
      chapter: wholeField(fields, 'chapter', 1, MAX_CHAPTER_NUMBER),
      index: numberField(fields, 'index'),
      exact: booleanField(fields, 'exact'),
      draw: numberField(fields, 'draw'),
      ...drawingFields(fields, path),
    };
  }
  if (kind === 'blank') {
    const fields = readFields(value, SLOT_BLANK_KEYS, 'a blank slot', path);
    return { kind, chapter: wholeField(fields, 'chapter', 1, MAX_CHAPTER_NUMBER) };
  }
  if (kind === 'none' || kind === 'pending') {
    readFields(value, SLOT_EMPTY_KEYS, `a ${kind} slot`, path);
    return { kind };
  }
  throw new ProtocolError(`${path} has an unknown kind "${kind.slice(0, 64)}"`);
}

/** V12: a spread answer's units, 1 to 3 of 1 or 2 slots (so at most 6 pages, within {@link MAX_VIEW_PAGES}). */
function unitsField(value: unknown): BookSlot[][] {
  if (!Array.isArray(value)) throw new ProtocolError('units must be an array');
  if (value.length === 0 || value.length > MAX_UNITS) throw new ProtocolError(`units must hold 1 to ${MAX_UNITS} units`);
  const units = value.map((unit: unknown, u): BookSlot[] => {
    if (!Array.isArray(unit)) throw new ProtocolError(`units.${u} must be an array`);
    if (unit.length === 0 || unit.length > MAX_SLOTS) throw new ProtocolError(`units.${u} must hold 1 to ${MAX_SLOTS} slots`);
    return unit.map((slot: unknown, s) => slotField(slot, `units.${u}.${s}`));
  });
  return units;
}

/** E4: a PDF's bytes: an `ArrayBuffer` of its own (transferred, not shared) that starts as a PDF does. */
function bytesField(fields: Fields): ArrayBuffer {
  const bytes = fields.get('bytes');
  if (!(bytes instanceof ArrayBuffer)) throw new ProtocolError('bytes must be an ArrayBuffer');
  const head = new Uint8Array(bytes, 0, Math.min(bytes.byteLength, PDF_HEADER.length));
  if (String.fromCharCode(...head) !== PDF_HEADER) throw new ProtocolError(`bytes must start with ${PDF_HEADER}`);
  return bytes;
}

/** Parses a response arriving from the layout worker (P1–P3, P5–P9, V6–V9, E3–E5). */
export function parseLayoutResponse(value: unknown): LayoutResponse {
  return parsing(() => {
    const kind = kindOf(value, 'layout response');
    if (kind === 'loaded') {
      const fields = readFields(value, LOADED_KEYS, 'a loaded response');
      return { id: idField(fields), kind, faces: faceList(fields.get('faces'), 'faces', loadedFace) };
    }
    if (kind === 'shaped') {
      const fields = readFields(value, SHAPED_KEYS, 'a shaped response');
      return { id: idField(fields), kind, run: runField(fields.get('run')) };
    }
    if (kind === 'done' || kind === 'stale') {
      const fields = readFields(value, ACK_KEYS, `a ${kind} response`);
      return { id: idField(fields), kind };
    }
    if (kind === 'spread') {
      const fields = readFields(value, SPREAD_ANSWER_KEYS, 'a spread response');
      const units = unitsField(fields.get('units'));
      const place = fields.get('place');
      return {
        id: idField(fields),
        kind,
        units,
        current: wholeField(fields, 'current', 0, units.length - 1),
        place: place === null ? null : placeField(place, 'place'),
        turned: wholeField(fields, 'turned', -MAX_TURN, MAX_TURN) + 0,
        start: booleanField(fields, 'start'),
        end: booleanField(fields, 'end'),
        unsettled: booleanField(fields, 'unsettled'),
      };
    }
    if (kind === 'settled') {
      const fields = readFields(value, SETTLED_KEYS, 'a settled response');
      return { id: idField(fields), kind, complete: booleanField(fields, 'complete') };
    }
    if (kind === 'progress') {
      const fields = readFields(value, PROGRESS_KEYS, 'a progress response');
      const total = numberField(fields, 'total');
      return { id: idField(fields), kind, done: wholeField(fields, 'done', 0, total), total };
    }
    if (kind === 'pdf') {
      const fields = readFields(value, BYTES_KEYS, 'a pdf response');
      return { id: idField(fields), kind, bytes: bytesField(fields) };
    }
    if (kind === 'cancelled') {
      const fields = readFields(value, ACK_KEYS, 'a cancelled response');
      return { id: idField(fields), kind };
    }
    if (kind === 'error') {
      const fields = readFields(value, ERROR_KEYS, 'an error response');
      const message = fields.get('message');
      if (typeof message !== 'string') throw new ProtocolError('message must be a string');
      return { id: idField(fields), kind, message: message.slice(0, MAX_MESSAGE_LENGTH) };
    }
    throw new ProtocolError(`unknown layout response "${kind.slice(0, 64)}"`);
  });
}

/**
 * The ID of a message that failed to parse, when it has a valid one, so the failure can still be answered or
 * routed to its request (P10); null otherwise.
 */
export function messageId(value: unknown): number | null {
  if (typeof value !== 'object' || value === null) return null;
  const descriptor = Object.getOwnPropertyDescriptor(value, 'id');
  const id: unknown = descriptor !== undefined && 'value' in descriptor ? descriptor.value : undefined;
  return typeof id === 'number' && Number.isSafeInteger(id) && id >= 0 ? id + 0 : null;
}
